import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const SettingsCtx = createContext(null);

export function SettingsProvider({ children }) {
    const [settings, setSettings] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/settings");
            setSettings(data);
            applyTheme(data);
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("settings load failed:", e);
            }
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = useCallback(async (patch) => {
        const merged = { ...settings, ...patch };
        const { data } = await api.put("/settings", {
            club_name: merged.club_name,
            logo_url: merged.logo_url || null,
            primary_hsl: merged.primary_hsl,
            accent_hsl: merged.accent_hsl,
            address: merged.address || null,
            current_season: merged.current_season || "2025/2026",
        });
        setSettings(data);
        applyTheme(data);
        return data;
    }, [settings]);

    const value = useMemo(
        () => ({ settings, save, reload: load }),
        [settings, save, load]
    );

    return (
        <SettingsCtx.Provider value={value}>{children}</SettingsCtx.Provider>
    );
}

function applyTheme(s) {
    if (!s) return;
    const root = document.documentElement;
    if (s.primary_hsl) root.style.setProperty("--theme-primary", s.primary_hsl);
    if (s.accent_hsl) root.style.setProperty("--theme-accent", s.accent_hsl);
    // Update PWA theme-color to match brand
    if (s.primary_hsl) {
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) meta.setAttribute("content", `hsl(${s.primary_hsl})`);
    }
}

export function useSettings() {
    return useContext(SettingsCtx);
}
